import {
  IEditedFieldsValue,
  IReportingOptions,
  IlogisticServiceDetails,
  IlogisticServiceLocation,
  TEditField
} from '../../../../../../types/services';
import { getLocationDisplayData } from '../../../../../../utils/locationIcons';
import { NudosButton } from '../../../../../../components/NudosComponents';
import { IconHouse } from '../../../../../../assets/DesignSystem/SVGComponents';

const ConfirmAddressChangeModalContent = ({
  logisticServiceData,
  closeModalCallback,
  submitModalCallback,
  location,
  loading,
  selectedReportingOptions,
  typeConfirmChanges,
  editedFields,
  valueOfEditedFields
}: {
  logisticServiceData?: IlogisticServiceDetails | null;
  closeModalCallback: () => void;
  submitModalCallback: () => void;
  location: 'origin' | 'destination';
  loading?: boolean;
  selectedReportingOptions: IReportingOptions[];
  typeConfirmChanges?: boolean;
  editedFields?: TEditField[];
  valueOfEditedFields?: IEditedFieldsValue;
}) => {
  const { originLocation, destinationLocation, deliveryInfo, collectionInfo } = logisticServiceData || {};

  const locationIsOrigin = location === 'origin';
  const originPlaceIsUser = originLocation?.place === 'user';
  const destinationPlaceIsUser = destinationLocation?.place === 'user';

  const noDataText = 'Sin datos';
  const logisticServiceLocationData = locationIsOrigin ? originLocation : destinationLocation;
  const { additionalReferences, address, country, place, city, zipCode } = logisticServiceLocationData || {};
  const logisticServiceCountry = country?.name || '';
  const originEmployeeName = collectionInfo?.delivererName;
  const destinationEmployeeName = deliveryInfo?.receiverName;
  const locationText = locationIsOrigin ? 'origen' : 'destino';
  const originDestinationText = `Información de ${locationText}`;
  const phone = location === 'origin' ? collectionInfo?.delivererPhone : deliveryInfo?.receiverPhone;

  // REPORTS
  const reportPhone = selectedReportingOptions?.find(report => report?.name === 'Número de teléfono');
  const reportCity = selectedReportingOptions?.find(report => report?.name === 'Ciudad');
  const reportAddress = selectedReportingOptions?.find(report => report?.name === 'Dirección');
  const reportIndications = selectedReportingOptions?.find(report => report?.name === 'Indicaciones');
  const reportZipCode = selectedReportingOptions?.find(report => report?.name === 'Código Postal');

  //EDITIONS
  const editionPhone = editedFields?.find(field => field === 'phone');
  const editionCity = editedFields?.find(field => field === 'city');
  const editionAddress = editedFields?.find(field => field === 'address');
  const editionAdditionalReferences = editedFields?.find(field => field === 'additionalReferences');
  const editionZipCode = editedFields?.find(field => field === 'zipCode');

  const locationData: IlogisticServiceLocation = {
    place: place,
    country: country || {}
  };

  const originDisplayData = getLocationDisplayData(locationData);
  const officeCountryName = `Oficina ${logisticServiceCountry}`;
  const originLocationName = originPlaceIsUser ? originEmployeeName : officeCountryName;
  const destinationLocationName = destinationPlaceIsUser ? destinationEmployeeName : officeCountryName;
  const locationName = locationIsOrigin ? originLocationName : destinationLocationName;

  return (
    <div id="confirmAddressChangeModal">
      <div className="confirmAddressChangeModalContainer">
        <div className="title">
          {typeConfirmChanges ? (
            <>
              Estás a punto de editar y notificar al cliente que editaste la información <br />
              de {locationText}
            </>
          ) : (
            <>
              Estás a punto de enviar un correo y notificar al cliente que la información de
              <br />
              {`${locationText} está incorrecta o incompleta`}
            </>
          )}
        </div>
        <div className="containerCard">
          <div className="modalHeaderContainer">
            <div className="nameAndIconContainer">
              <div className="iconContainer">
                {originDisplayData.icon}
                <div className="flag" style={{ backgroundImage: originDisplayData.flag }} />
              </div>
              {locationName}
            </div>
            <div className="originDestination">{originDestinationText}</div>
          </div>
          <div className="dividerSectionLine" />
          <div className="firstLine">
            {(reportCity || editionCity) && (
              <div className="cityContianer">
                <div className="subtitleItem">Ciudad</div>
                <div className="valueItem">
                  {editionCity && valueOfEditedFields?.city ? valueOfEditedFields?.city : city}
                </div>
              </div>
            )}
            {(reportZipCode || editionZipCode) && (
              <div className="postalCodeContainer">
                <div className="subtitleItem">Código postal</div>
                <div className="valueItem">
                  {editionZipCode && valueOfEditedFields?.zipCode ? valueOfEditedFields?.zipCode : zipCode}
                </div>
              </div>
            )}
            {(reportPhone || editionPhone) && (
              <div className="numberContainer">
                <div className="subtitleItem">Número de teléfono</div>
                <div className="valueItem">
                  {editionPhone && valueOfEditedFields?.phone ? valueOfEditedFields?.phone : phone}
                </div>
              </div>
            )}
          </div>
          {(reportAddress || editionAddress) && (
            <div className="secondLine">
              <div className="subtitleItem">Dirección</div>
              <div className="iconAndText">
                <div className="iconAddress">
                  <IconHouse />
                </div>
                <div className="infoAddressContainer">
                  <div className="valueItem">
                    {editionAddress && valueOfEditedFields?.address
                      ? valueOfEditedFields?.address
                      : address || noDataText}
                  </div>
                </div>
              </div>
            </div>
          )}
          {(reportIndications || editionAdditionalReferences) && (
            <div className="thirdLine">
              <div className="subtitleItem">Indicaciones</div>
              <div className="valueItem">
                {editionAdditionalReferences && valueOfEditedFields?.additionalReferences
                  ? valueOfEditedFields?.additionalReferences
                  : additionalReferences || noDataText}{' '}
              </div>
            </div>
          )}
        </div>
        <div className="finalItemsContainer">
          <div className="questionText">¿Seguro que deseas continuar?</div>
          <div className="containerButtons">
            <NudosButton
              handleClick={() => closeModalCallback()}
              colorPalette="secondary"
              customClassName="firstButton"
              buttonText="Cerrar"
            />
            <NudosButton
              handleClick={() => submitModalCallback()}
              customClassName="secondButton"
              buttonText={`${typeConfirmChanges ? 'Editar información' : 'Enviar correo'}`}
              isButtonLoading={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAddressChangeModalContent;
